import { cn } from '@/lib/utils';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';

interface KPICardProps {
  title: string;
  value: string;
  trend?: {
    value: number;
    isPositive: boolean;
  };
  variant: 'careful' | 'accent' | 'basic' | 'neutral';
  icon?: React.ReactNode;
  subtitle?: string;
}

const variantStyles = {
  careful: {
    bg: 'bg-[hsl(var(--careful-50))]',
    border: 'border-[hsl(var(--careful-100))]',
    text: 'text-[hsl(var(--careful-700))]',
    label: 'text-[hsl(var(--careful-600))]',
    trendBg: 'bg-[hsl(var(--careful-100))]',
  },
  accent: {
    bg: 'bg-[hsl(var(--accent-50))]',
    border: 'border-[hsl(var(--accent-100))]',
    text: 'text-[hsl(var(--accent-700))]',
    label: 'text-[hsl(var(--accent-600))]',
    trendBg: 'bg-[hsl(var(--accent-100))]',
  },
  basic: {
    bg: 'bg-[hsl(var(--basic-50))]',
    border: 'border-[hsl(var(--basic-100))]',
    text: 'text-[hsl(var(--basic-700))]',
    label: 'text-[hsl(var(--basic-600))]',
    trendBg: 'bg-[hsl(var(--basic-100))]',
  },
  neutral: {
    bg: 'bg-card',
    border: 'border-border',
    text: 'text-foreground',
    label: 'text-muted-foreground',
    trendBg: 'bg-muted',
  },
};

export function KPICard({ title, value, trend, variant, icon, subtitle }: KPICardProps) {
  const styles = variantStyles[variant];
  
  const TrendIcon = trend 
    ? (trend.value === 0 ? Minus : (trend.isPositive ? TrendingUp : TrendingDown))
    : null;

  return (
    <div 
      className={cn(
        'relative p-5 rounded-2xl border overflow-hidden transition-all duration-300',
        'hover:shadow-lg hover:-translate-y-0.5',
        styles.bg,
        styles.border
      )}
    >
      {/* Background decoration */}
      <div className="absolute -right-4 -top-4 w-24 h-24 rounded-full opacity-10 bg-current" />
      
      <div className="relative z-10">
        <div className="flex items-center justify-between mb-2">
          <span className={cn('text-xs font-semibold uppercase tracking-wider', styles.label)}>
            {title}
          </span>
          {icon && (
            <div className={cn('p-1.5 rounded-lg', styles.trendBg)}>
              {icon}
            </div>
          )}
        </div>
        
        <h2 className={cn('text-2xl md:text-3xl font-black tracking-tight', styles.text)}>
          {value}
        </h2>
        
        {(trend || subtitle) && (
          <div className="mt-2 flex items-center gap-2">
            {trend && TrendIcon && (
              <span 
                className={cn(
                  'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold',
                  trend.isPositive 
                    ? 'bg-[hsl(var(--accent-100))] text-[hsl(var(--accent-700))]'
                    : 'bg-[hsl(var(--careful-100))] text-[hsl(var(--careful-700))]'
                )}
              >
                <TrendIcon className="h-3 w-3" />
                {trend.value > 0 ? '+' : ''}{trend.value}%
              </span>
            )}
            {subtitle && (
              <span className="text-xs text-muted-foreground">{subtitle}</span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
